export default {
  terms: {
    title: 'Terms of Service',
    effectiveDate: 'Effective date: March 15, 2024',
    intro:
      'Welcome to PhotoScope. PhotoScope is an AI-powered visual learning tool that helps photography beginners and enthusiasts understand the visual style of a photograph by comparing your photo with a reference image you choose, then explains the differences and suggests directions for your own editing. These Terms of Service ("Terms") describe your rights and responsibilities when you use PhotoScope.',
    sections: [
      {
        heading: '1. Acceptance of Terms',
        body: [
          'By creating an account or using PhotoScope, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, please do not use the service.',
        ],
      },
      {
        heading: '2. Description of the Service',
        body: [
          'PhotoScope lets you upload a photo and select a reference image. We analyze the visual gap between them—such as tone, color, saturation, and composition—and provide AI-generated explanations and editing suggestions.',
          'PhotoScope is an educational and learning tool. It is designed to help you understand photography and develop your own judgment. It is not an automatic photo editor, a retouching service, or a guarantee that any result will match a reference image.',
          'We may update features, models, and analysis methods over time. Previews, charts, and suggestions are provided for learning purposes and may change without notice.',
        ],
      },
      {
        heading: '3. Eligibility',
        body: [
          'You must be at least 13 years old to use PhotoScope. By using the service, you represent and warrant that you meet this requirement and have the capacity to agree to these Terms.',
        ],
      },
      {
        heading: '4. Accounts and Third-Party Sign-In',
        body: [
          'You may create a PhotoScope account with your email or sign in with a third-party provider such as Google. If you use third-party sign-in, your use is also subject to that provider’s terms.',
          'You are responsible for keeping your account credentials confidential and for all activity that occurs under your account. Notify us immediately if you suspect unauthorized access.',
        ],
      },
      {
        heading: '5. Credits and Billing',
        body: [
          'Some PhotoScope features use a credits system. Your account may include free credits and credits you purchase.',
          'One-time purchased credits have an expiration date, which is shown when you buy them and in your dashboard. Unused credits expire on that date and are not renewed automatically.',
          'Prices for credits are shown at the time of purchase and may change. Changes apply to future purchases, not to credits already bought.',
          'Except where required by law, purchased credits are non-refundable. If you have a question about a charge, contact us and we will review it.',
        ],
        items: [
          'You can view your credit balance and expiry at any time in your dashboard.',
          'We may offer promotional or bonus credits; these may have separate terms shown when granted.',
        ],
      },
      {
        heading: '6. Your Content and Reference Images',
        body: [
          'You may upload your own photos ("Your Content") and reference images. You retain ownership of Your Content. By uploading, you grant PhotoScope a limited, non-exclusive, royalty-free license to process, analyze, and store that content solely to provide and improve the service.',
          'You represent and warrant that you have the rights to upload Your Content and any reference image, and that they do not infringe others’ rights or violate applicable laws.',
        ],
      },
      {
        heading: '7. Acceptable Use',
        body: [
          'You agree to use PhotoScope only for lawful purposes. In particular, you will not:',
        ],
        items: [
          'Upload unlawful, infringing, or harmful content, including images that violate others’ privacy or intellectual property.',
          'Attempt to reverse engineer, disrupt, or overload the service, or gain unauthorized access to its systems.',
          'Use the service to build a competing product or scrape its outputs at scale.',
        ],
      },
      {
        heading: '8. Intellectual Property',
        body: [
          'The PhotoScope software, designs, trademarks, and analyses (except your own inputs) are owned by us or our licensors. You may not use them without our prior written permission.',
          'AI-generated explanations, charts, and suggestions are provided to you for your personal learning and editing. They do not transfer ownership of any reference image to you.',
        ],
      },
      {
        heading: '9. AI-Generated Content and Disclaimer',
        body: [
          'PhotoScope uses artificial intelligence to generate explanations, gap analysis, and editing suggestions. These outputs are guidance based on visual data and photographic principles; they are not professional photography, editing, or any other professional advice.',
          'We do not guarantee that any suggestion is accurate, complete, or suitable for your specific photo or intent. You are responsible for your own editing decisions and the final images you create or share.',
          'The service is provided "as is" without warranties of any kind to the maximum extent permitted by law.',
        ],
      },
      {
        heading: '10. Limitation of Liability',
        body: [
          'To the fullest extent permitted by law, PhotoScope is not liable for any indirect, incidental, or consequential damages arising from your use of the service, including loss of data or any decision made based on AI suggestions.',
        ],
      },
      {
        heading: '11. Termination',
        body: [
          'We may suspend or terminate your access if you breach these Terms or misuse the service. You may stop using PhotoScope at any time. Upon termination, your right to use the service ends immediately.',
        ],
      },
      {
        heading: '12. Changes to These Terms',
        body: [
          'We may update these Terms from time to time. We will post the updated version with a new effective date. Continued use after changes means you accept the updated Terms.',
        ],
      },
      {
        heading: '13. Governing Law',
        body: [
          'These Terms are governed by the laws of the United States. Any dispute will be resolved in the courts of the United States. If you are located elsewhere, you remain responsible for complying with local laws.',
        ],
      },
      {
        heading: '14. Contact Us',
        body: [
          'Questions about these Terms? Contact us at info@photoscope.cc.',
        ],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    effectiveDate: 'Effective date: March 15, 2024',
    intro:
      'This Privacy Policy explains what information PhotoScope collects, how we use it, and the choices you have. PhotoScope is an AI visual learning tool: you upload a photo and a reference image, and we analyze the visual gap between them to help you learn.',
    sections: [
      {
        heading: '1. Information We Collect',
        body: [],
        sub: [
          {
            heading: '1.1 Account Information',
            body: [
              'When you register, we collect your name, email address, and account details. If you sign in with Google or another provider, we receive the account identifiers and basic profile information that the provider shares.',
            ],
          },
          {
            heading: '1.2 Images and Reference Images',
            body: [
              'We collect the photos you upload and any reference images you choose, along with technical metadata needed for analysis (such as dimensions).',
            ],
          },
          {
            heading: '1.3 Usage and Device Data',
            body: [
              'We collect information about how you use PhotoScope, including IP address, browser type, device information, and analytics about features you use. This helps us operate and improve the service.',
            ],
          },
        ],
      },
      {
        heading: '2. How We Process Your Images',
        body: [
          'To generate analysis, your images are processed using on-device techniques (such as local image analysis in your browser) and, where needed, cloud-based AI services. Reference images are used only to compute the style gap for your requested comparison.',
          'We use your images solely to provide the analysis you request and to maintain your history. We do not use your personal photos to train public models without your separate consent.',
        ],
      },
      {
        heading: '3. How We Use Your Information',
        body: [
          'We use your information to:',
        ],
        items: [
          'Provide, operate, and improve PhotoScope.',
          'Process and analyze the images you upload.',
          'Communicate with you about your account, updates, or support.',
          'Keep the service secure and prevent misuse.',
        ],
      },
      {
        heading: '4. Sharing and Disclosure',
        body: [
          'We do not sell your personal information. We may share it with:',
        ],
        items: [
          'Service providers that operate PhotoScope, such as cloud hosting, AI processing, and analytics partners, under appropriate safeguards.',
          'Authorities where required by law or to protect our rights and users.',
          'Aggregated or anonymized data that cannot identify you, for analysis and product improvement.',
        ],
      },
      {
        heading: '5. Cookies and Tracking',
        body: [
          'We use cookies and similar technologies to keep you signed in, remember preferences, and understand usage. You can manage cookies in your browser settings.',
        ],
      },
      {
        heading: '6. Data Security',
        body: [
          'We use industry-standard measures to protect your information. No system is completely secure, so please use the service with care and protect your account credentials.',
        ],
      },
      {
        heading: '7. Data Retention and Deletion',
        body: [
          'We keep your information only as long as needed:',
        ],
        items: [
          'Account data: retained while your account is active.',
          'Uploaded images and analysis: retained to provide the service and your history, and deleted when you delete your account or request deletion.',
          'You may request deletion of your data by contacting us. We will act on verifiable requests within a reasonable time.',
        ],
      },
      {
        heading: '8. Your Rights',
        body: [
          'Depending on where you live, you may have rights to access, correct, delete, or port your personal data, and to object to or opt out of certain processing. To exercise these rights, contact us. We will respond as required by applicable law (such as GDPR or CCPA).',
        ],
      },
      {
        heading: '9. Children’s Privacy',
        body: [
          'PhotoScope is not intended for children under 13. We do not knowingly collect personal information from children under 13.',
        ],
      },
      {
        heading: '10. International Data Transfers',
        body: [
          'PhotoScope may process data on servers outside your country, including in the United States. Where we transfer personal data across borders, we use appropriate safeguards.',
        ],
      },
      {
        heading: '11. Changes to This Policy',
        body: [
          'We may update this Privacy Policy from time to time. We will post the new version with an updated effective date. Significant changes will be communicated through the website.',
        ],
      },
      {
        heading: '12. Contact Us',
        body: [
          'Questions about privacy? Contact us at info@photoscope.cc.',
        ],
      },
    ],
  },
};
